"use client";

import S from "./index.module.scss";
import Input from "@/components/ui/input";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { yupFeedbackSchema } from "@/api/feedback/schemas";
import Textarea from "@/components/ui/textarea";
import { FC, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import PhoneInput from "@/components/ui/phone-input";
import { useMutation } from "@tanstack/react-query";
import { ApiFeedback } from "@/api/feedback";
import CustomIcon from "@/components/ui/custom-icon";
import Select from "@/components/ui/select";
import { useLocale } from "next-intl";
import { downloadHandler, openErrorNotification, openSuccessFormNotification } from "@/utils/helpers";
import { ApplicationResource } from "@/api/generated-api";

interface IFeedback {
  onClose(): void;
}

const Feedback: FC<IFeedback> = ({ onClose }) => {
  const lang = useLocale();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    clearErrors,
    watch,
  } = useForm<ApplicationResource>({ resolver: yupResolver(yupFeedbackSchema) });
  const [selectCity, setSelectCity] = useState("");

  useEffect(() => {
    if (watch("phone")?.length === 18) {
      clearErrors("phone");
    }
  }, [watch("phone")]);

  const postForm = useMutation({
    mutationFn: (data: ApplicationResource) => {
      return ApiFeedback.postFeedback(lang, {
        name: data.name,
        phone: data.phone,
        email: data.email,
        message: data.message,
        city: selectCity || data.city,
      });
    },
    onSuccess: () => {
      openSuccessFormNotification({ title: "Форма успешно отправлена!" });
      onClose();
    },
    onError: () => {
      openErrorNotification({ title: "Что-то пошло не так" });
    },
  });

  const onSubmit: SubmitHandler<ApplicationResource> = data => {
    postForm.mutate(data);
  };

  const selectHandler = (city: string) => {
    if (city !== selectCity) {
      setSelectCity(city);
    }
  };

  return (
    <div className={S.background}>
      <div className={S.contentContainer}>
        <button onClick={onClose} className={S.closeButton}>
          <CustomIcon fileName="icon-btn-close" />
        </button>
        <h2 className={S.title}>Обратная связь</h2>
        <form className={S.form} onSubmit={handleSubmit(onSubmit)}>
          <Input {...register("name")} label="Ваше имя:" required />
          <div className={S.twoInRow}>
            <Input {...register("email")} label="E-mail:" required />
            <PhoneInput
              value={watch("phone")}
              setValue={value => setValue("phone", value)}
              error={errors.phone?.message}
              label="Номер телефона:"
            />
          </div>

          <div className={S.twoInRow}>
            <Select
              label={"Город:"}
              value={selectCity}
              setValue={value => selectHandler(value)}
              items={[
                { name: "", label: "Выберите из списка" },
                { name: "Москва", label: "Москва" },
                { name: "Санкт-Петербург", label: "Санкт-Петербург" },
              ]}
            />
            <Input disabled={!!selectCity} {...register("city")} label={"Заполните, если вашего города нет в списке"} />
          </div>

          <Textarea
            required
            label="Текст сообщения:"
            register={register("message")}
            error={errors.message?.message}
            placeholder="Введите сообщение"
          />
          <div className={S.submitBlock}>
            <span className={S.submitDescription}>
              Нажимая кнопку «Отправить», вы соглашаетесь с условиями{" "}
              <span
                className={S.submitDescriptionLink}
                onClick={() => downloadHandler("/files/policy/Политика_конфиденциальности_для_aquaartgroup.PDF")}
              >
                Политики конфиденциальности
              </span>
            </span>
            <Button variant="trBlackToBlack" type="submit">
              Отправить
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Feedback;
